import React, { useCallback, useContext, useEffect, useState } from 'react';
import { createContext } from 'react';
import { Coordinate } from '../types/floor';
import { RoomPlacingModel, RoomObjectType } from '../types/room';

export type NFTContextType = {
  filteredNFTs: RoomObjectType[];
  setFilteredNFTs: (n: RoomObjectType[]) => void;
  selectedNFT: RoomObjectType | undefined;
  setSelectedNFT: (n: RoomObjectType | undefined) => void;
  tempSelectedNFTImage: string;
  setTempSelectedNFTImage: (n: string) => void;
  isNFTPlacingMode: boolean;
  setNFTPlacingMode: (m: boolean) => void;
  nftBeingPlaced: RoomObjectType | undefined;
  setNFTBeingPlaced: (n: RoomObjectType | undefined) => void;
  isNFTPlacingValid: boolean;
  setNFTPlacingValid: (m: boolean) => void;
  nftPlacingModel: RoomPlacingModel | undefined;
  setNFTPlacingModel: (model: RoomPlacingModel) => void;
  nftPlacingCells: Coordinate[];
  setNFTPlacingCells: (cells: Coordinate[]) => void;
  nftPlaceableCellsAmountOnCurrentFloor: number;
  setNFTPlaceableCellsAmountOnCurrentFloor: (num: number) => void;
};

const defaultValue = {
  filteredNFTs: [],
  setFilteredNFTs: () => {},
  selectedNFT: undefined,
  setSelectedNFT: () => {},
  tempSelectedNFTImage: '',
  setTempSelectedNFTImage: (n: string) => {},
  nftBeingPlaced: undefined,
  setNFTBeingPlaced: () => {},
  isNFTPlacingMode: false,
  setNFTPlacingMode: () => {},
  isNFTPlacingValid: false,
  setNFTPlacingValid: () => {},
  nftPlacingModel: undefined,
  setNFTPlacingModel: () => {},
  nftPlacingCells: [],
  setNFTPlacingCells: () => {},
  nftPlaceableCellsAmountOnCurrentFloor: 0,
  setNFTPlaceableCellsAmountOnCurrentFloor: () => {},
};

export const NFTContext = createContext<NFTContextType>(defaultValue);

type Props = {
  children: any;
};

export const NFTContextProvider = ({ children }: Props) => {
  const [filteredNFTs, setFilteredNFTs] = useState<RoomObjectType[]>([]);
  const [selectedNFT, setSelectedNFT] = useState<RoomObjectType | undefined>(
    undefined
  );
  const [tempSelectedNFTImage, setTempSelectedNFTImage] = useState<string>('');

  useEffect(() => {
    if (!selectedNFT) {
      setTempSelectedNFTImage('');
    }
  }, [selectedNFT]);

  const manuallySetFilteredNFTs = useCallback((nfts: RoomObjectType[]) => {
    setFilteredNFTs(nfts);
  }, []);

  const [nftBeingPlaced, setNFTBeingPlaced] = useState<
    RoomObjectType | undefined
  >(undefined);
  const [nftPlacingModel, setNFTPlacingModel] = useState<
    RoomPlacingModel | undefined
  >(undefined);
  const [isNFTPlacingMode, setNFTPlacingMode] = useState<boolean>(false);
  const [isNFTPlacingValid, setNFTPlacingValid] = useState<boolean>(false);
  const [nftPlacingCells, setNFTPlacingCells] = useState<Coordinate[]>([]);

  const [
    nftPlaceableCellsAmountOnCurrentFloor,
    setNFTPlaceableCellsAmountOnCurrentFloor,
  ] = useState(0);

  return (
    <NFTContext.Provider
      value={{
        filteredNFTs,
        setFilteredNFTs: manuallySetFilteredNFTs,
        selectedNFT,
        setSelectedNFT,

        tempSelectedNFTImage,
        setTempSelectedNFTImage,

        nftBeingPlaced,
        setNFTBeingPlaced,
        isNFTPlacingValid,
        setNFTPlacingValid,
        nftPlacingModel,
        setNFTPlacingModel,
        isNFTPlacingMode,
        setNFTPlacingMode,
        nftPlacingCells,
        setNFTPlacingCells,
        nftPlaceableCellsAmountOnCurrentFloor,
        setNFTPlaceableCellsAmountOnCurrentFloor,
      }}
    >
      {children}
    </NFTContext.Provider>
  );
};

export function useNFTContext() {
  const context = useContext(NFTContext);
  if (context === undefined) {
    throw new Error('NFT context must be used within a Provider');
  }
  return context;
}
